import { formatDistanceToNow } from "date-fns";
import { StarRating } from "@/components/star-rating";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { Rating, Profile } from "@/lib/types";

interface RatingWithProfile extends Rating {
  from_profile: Pick<Profile, 'id' | 'name' | 'avatar_url'>;
}

interface RatingsListProps {
  ratings: RatingWithProfile[];
  title?: string;
  emptyMessage?: string;
}

export function RatingsList({ ratings, title, emptyMessage }: RatingsListProps) {
  if (ratings.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            {emptyMessage || "No ratings yet."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {title && (
        <h3 className="text-lg font-semibold">{title}</h3>
      )}
      
      {ratings.map((rating) => (
        <Card key={rating.id}>
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10">
                {rating.from_profile.avatar_url ? (
                  <img 
                    src={rating.from_profile.avatar_url} 
                    alt={rating.from_profile.name || ""}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <AvatarFallback>
                    {rating.from_profile.name?.charAt(0).toUpperCase() || "?"}
                  </AvatarFallback>
                )}
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium truncate">
                    {rating.from_profile.name || "Anonymous"}
                  </p>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(rating.created_at), { addSuffix: true })}
                  </span>
                </div>
                
                <div className="mt-1">
                  <StarRating rating={rating.score} size="sm" />
                </div>
              </div>
            </div>
          </CardHeader>
          
          {rating.comment && (
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {rating.comment}
              </p>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}